/**
 * Fällige Karten als Zahl am App-Symbol (Badging API).
 *
 * Grenze, die man kennen muss: die Zahl wird nur gesetzt, während die App
 * läuft. iOS kennt keinen Hintergrunddienst, der sie über Nacht nachzieht —
 * dafür bräuchte es Push-Nachrichten, und die App ist nur über Tailscale
 * erreichbar. Nach dem Lernen stimmt die Zahl also, am nächsten Morgen zeigt
 * sie noch den Stand von gestern Abend. Sie ist damit eine Erinnerung, keine
 * Uhr.
 *
 * Auf dem iPhone verlangt Safari zusätzlich die Erlaubnis für Mitteilungen,
 * bevor ein Abzeichen erscheint — obwohl keine einzige Mitteilung geschickt
 * wird. Deshalb wird sie beim Einschalten erfragt und nicht beim Start.
 */

const SCHLUESSEL = 'karteikarten:abzeichen'

/** Kann dieser Browser überhaupt ein Abzeichen setzen? */
export function abzeichenMoeglich() {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator
}

/** Hat der Nutzer das Abzeichen eingeschaltet? */
export function abzeichenAn() {
  try {
    return localStorage.getItem(SCHLUESSEL) === 'an'
  } catch {
    return false
  }
}

function merke(an) {
  try {
    if (an) localStorage.setItem(SCHLUESSEL, 'an')
    else localStorage.removeItem(SCHLUESSEL)
  } catch {
    // Privater Modus: die Einstellung gilt dann nur für diese Sitzung.
  }
}

/** Erlaubnis für Mitteilungen — auf dem iPhone Voraussetzung fürs Abzeichen. */
export async function erlaubnisHolen() {
  if (typeof Notification === 'undefined') return 'nicht-noetig'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  try {
    return await Notification.requestPermission()
  } catch {
    return 'denied'
  }
}

/**
 * Abzeichen einschalten. Liefert, woran es gegebenenfalls scheitert, damit die
 * Einstellungsseite es sagen kann statt still nichts zu tun.
 *
 * Erst versuchen, dann fragen: am Schreibtisch setzt Chrome das Abzeichen ohne
 * jede Erlaubnis, und ein Dialog für Mitteilungen wäre dort eine Zumutung für
 * nichts. Nur wo der Versuch abgewiesen wird — auf dem iPhone —, kommt die
 * Frage nach der Erlaubnis, und danach ein zweiter Versuch.
 */
export async function abzeichenEinschalten() {
  if (!abzeichenMoeglich()) return { an: false, grund: 'nicht-unterstuetzt' }

  const probe = () => navigator.setAppBadge(1).then(() => navigator.clearAppBadge?.())

  try {
    await probe()
  } catch {
    const erlaubnis = await erlaubnisHolen()
    if (erlaubnis === 'denied') return { an: false, grund: 'abgelehnt' }
    try {
      await probe()
    } catch {
      return { an: false, grund: 'abgelehnt' }
    }
  }

  merke(true)
  return { an: true, grund: null }
}

export function abzeichenAusschalten() {
  merke(false)
  if (abzeichenMoeglich()) navigator.clearAppBadge?.().catch(() => {})
}

/**
 * Zahl ans Symbol schreiben. 0 räumt es ab — eine „0" am Symbol wäre ein
 * Fleck ohne Aussage.
 */
export function setzeAbzeichen(anzahl) {
  if (!abzeichenMoeglich() || !abzeichenAn()) return
  const n = Math.max(0, Math.trunc(Number(anzahl) || 0))
  const versprechen = n > 0 ? navigator.setAppBadge(n) : navigator.clearAppBadge?.()
  // Scheitert still: ein fehlendes Abzeichen darf keinen Bildschirm stören.
  versprechen?.catch(() => {})
}
